import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton, TextField } from '@mui/material';
import { observer } from 'mobx-react';
import { StatusChip } from '@pages/customer/common/statusChip/StatusChip';
import { useStore } from '@stores/RootStore';
import { ChevronIcon } from '@icons/ChevronIcon';
import { useGetApproval, useUpdateApprovalStepStatus } from '@queryHooks/useApproval';
import { useGetSubscriptionUseRequest } from '@queryHooks/useSubscriptionUseRequest';
import { dateFormat } from '@utils/dateUtil';
import * as S from './ApprovalDetailStyles';

export const ApprovalDetail = observer(() => {
  const { t } = useTranslation();
  const {
    uiStore: { alertStore },
    authStore,
  } = useStore();
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: approval } = useGetApproval(params.approvalId as string);
  const { mutate: updateApprovalStepStatus } = useUpdateApprovalStepStatus();
  const { data: subscriptionUseRequest } = useGetSubscriptionUseRequest(approval?.targetId ?? '', {
    enabled: approval && approval.type === 'SUBSCRIPTION_USE_REQUEST',
  });

  const [comment, setComment] = useState('');
  const [selectedAction, setSelectedAction] = useState<'APPROVED' | 'REJECTED' | null>(null);

  const currentStep = approval?.currentStep;

  const handleApprovalAction = (action: 'APPROVED' | 'REJECTED') => {
    if (!currentStep || !approval) return;

    updateApprovalStepStatus(
      {
        approvalId: approval.id,
        approvalStepId: currentStep.id,
        dto: {
          status: action,
          comment: comment.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          alertStore.open({
            title: t(action === 'APPROVED' ? 'Approval_Action_Approve_Complete' : 'Approval_Action_Reject_Complete'),
            message: t(action === 'APPROVED' ? 'Approval_Action_Approve_Message' : 'Approval_Action_Reject_Message'),
            confirmName: t('Confirm'),
            type: 'success',
            onConfirm: () => navigate(-1),
          });
        },
      },
    );
  };

  const handleActionSelect = (action: 'APPROVED' | 'REJECTED') => {
    setSelectedAction(action);
  };

  const handleClickProcessBtn = () => {
    if (!selectedAction) return;

    alertStore.open({
      title: t(
        selectedAction === 'APPROVED'
          ? 'Approval_Action_Approve_Confirm_Title'
          : 'Approval_Action_Reject_Confirm_Title',
      ),
      message: t(
        selectedAction === 'APPROVED'
          ? 'Approval_Action_Approve_Confirm_Message'
          : 'Approval_Action_Reject_Confirm_Message',
      ),
      confirmName: t(selectedAction === 'APPROVED' ? 'Approval_Action_Approve' : 'Approval_Action_Reject'),
      cancelName: t('Cancel'),
      useCancel: true,
      type: selectedAction === 'APPROVED' ? 'success' : 'error',
      onConfirm: () => handleApprovalAction(selectedAction),
    });
  };

  if (!approval) {
    return <div>{t('Loading')}</div>;
  }

  return (
    <S.Container>
      <S.Header>
        <IconButton onClick={() => navigate(-1)}>
          <ChevronIcon rotateNum={270} />
        </IconButton>
        <span>{t('Approval_Detail_Title')}</span>
      </S.Header>
      <S.Body>
        {/* 신청 내역 */}
        <S.TitleHeader>
          <span className="title">{t('Approval_Detail_Application_History')}</span>
        </S.TitleHeader>
        <S.Frame>
          <S.Columns>
            <S.Column divide>
              <span className="content-name">{t('Approval_Detail_Application_Date')}</span>
              <span className="content-value">{approval.createdDate}</span>
            </S.Column>
            <S.Column divide={false}>
              <span className="content-name">{t('Approval_Detail_Item_Name')}</span>
              <span className="content-value">{approval.typeStr}</span>
            </S.Column>
          </S.Columns>
          <S.Columns>
            <S.Column divide>
              <span className="content-name">{t('Approval_Detail_Applicant')}</span>
              <span className="content-value">{approval.drafterName}</span>
            </S.Column>
            <S.Column divide={false}>
              <span className="content-name">{t('Approval_Detail_Final_Approver')}</span>
              <span className="content-value">{approval.finalApproverName || '-'}</span>
            </S.Column>
          </S.Columns>
        </S.Frame>

        {/* 항목 상세 */}
        {approval.type === 'SUBSCRIPTION_USE_REQUEST' && (
          <>
            <S.TitleHeader>
              <span className="title">{t('Approval_Detail_Item_Details')}</span>
            </S.TitleHeader>
            <S.Frame>
              <S.Columns>
                <S.Column divide>
                  <span className="content-name">{t('Approval_Detail_Supplier')}</span>
                  <span className="content-value">
                    {subscriptionUseRequest?.subscription?.product.companyName ?? '-'}
                  </span>
                </S.Column>
                <S.Column divide={false}>
                  <span className="content-name">{t('Approval_Detail_Product_Name')}</span>
                  <span className="content-value">{subscriptionUseRequest?.subscription?.product.name ?? '-'}</span>
                </S.Column>
              </S.Columns>
              <S.Columns>
                <S.Column divide>
                  <span className="content-name">{t('Approval_Detail_Plan')}</span>
                  <span className="content-value"> {subscriptionUseRequest?.planName ?? '-'} </span>
                </S.Column>
                <S.Column divide={false}>
                  <span className="content-name">{t('Approval_Detail_Subscription')}</span>
                  <span className="content-value">{subscriptionUseRequest?.subscription?.name ?? '-'}</span>
                </S.Column>
              </S.Columns>
              <S.Columns>
                <S.Column divide>
                  <span className="content-name">{t('Approval_Detail_Usage_Period')}</span>
                  <span className="inquiry">
                    {`${dateFormat(subscriptionUseRequest?.startDate, '-', t('DateFormat_YMD'))} ~ ${dateFormat(
                      subscriptionUseRequest?.endDate,
                      '-',
                      t('DateFormat_YMD'),
                    )}`}
                  </span>
                </S.Column>
              </S.Columns>
              <S.Columns>
                <S.Column divide>
                  <span className="content-name">{t('Approval_Detail_Content')}</span>
                  <span className="inquiry">{subscriptionUseRequest?.inquiry ?? '-'}</span>
                </S.Column>
              </S.Columns>
            </S.Frame>
          </>
        )}
        {/* 승인 프로세스 내역 */}
        <S.TitleHeader>
          <span className="title">{t('Approval_Detail_Process_History')}</span>
        </S.TitleHeader>
        <S.Frame>
          {/* 기안 단계 */}
          <S.Columns>
            <S.Column divide={false}>
              <ApprovalStepContainer>
                <ApprovalStepNumber>0</ApprovalStepNumber>
                <ApprovalStepContent>
                  <ApproverInfo>{approval.drafterName}</ApproverInfo>
                  <ApprovalStepStatusContainer>
                    <StatusChip
                      status={t('Approval_Status_Draft')}
                      backgroundColor={theme.colors['bg-gray-dark']}
                      color={theme.colors['text-white']}
                    />
                    <ApprovalStepUpdatedAt>{approval.createdDate}</ApprovalStepUpdatedAt>
                  </ApprovalStepStatusContainer>
                </ApprovalStepContent>
              </ApprovalStepContainer>
            </S.Column>
          </S.Columns>

          {/* 승인 단계들 */}
          {approval.steps
            .sort((a, b) => a.stepOrder - b.stepOrder)
            .map(step => (
              <S.Columns key={step.id}>
                <S.Column divide={false}>
                  <ApprovalStepContainer>
                    <ApprovalStepNumber>{step.stepOrder + 1}</ApprovalStepNumber>
                    <ApprovalStepContent>
                      <ApproverInfo>{step.approverInfoLabel}</ApproverInfo>
                      {step.status !== 'WAITING' && (
                        <>
                          <ApprovalStepStatusContainer>
                            <StatusChip
                              status={step.statusLabel}
                              backgroundColor={
                                theme.colors[step.statusBackgroundColor as keyof typeof theme.colors] as string
                              }
                              color={theme.colors[step.statusTextColor as keyof typeof theme.colors] as string}
                            />
                            <ApprovalStepUpdatedAt>{step.approvedAt || '-'}</ApprovalStepUpdatedAt>
                          </ApprovalStepStatusContainer>
                          {step.comment && <ApprovalStepComment>{step.comment}</ApprovalStepComment>}
                        </>
                      )}
                    </ApprovalStepContent>
                  </ApprovalStepContainer>
                </S.Column>
              </S.Columns>
            ))}
        </S.Frame>

        {/* 신청 처리 - 현재 사용자가 승인자인 경우에만 표시 */}
        {approval.steps.filter(step => step.approverId === authStore.id && step.status === 'IN_PROGRESS').length >
          0 && (
          <>
            <S.TitleHeader>
              <span className="title">{t('Approval_Detail_Process_Action')}</span>
            </S.TitleHeader>
            {approval.steps.length > 0 && (
              <S.ProcessFrame>
                <S.ButtonSection>
                  <S.ActionButtonGroup>
                    <S.ActionButton
                      variant={selectedAction === 'APPROVED' ? 'contain' : 'outline'}
                      onClick={() => handleActionSelect('APPROVED')}
                      selected={selectedAction === 'APPROVED'}
                    >
                      {t('Approval_Action_Approve')}
                    </S.ActionButton>
                    <S.ActionButton
                      variant={selectedAction === 'REJECTED' ? 'contain' : 'outline'}
                      onClick={() => handleActionSelect('REJECTED')}
                      selected={selectedAction === 'REJECTED'}
                    >
                      {t('Approval_Action_Reject')}
                    </S.ActionButton>
                  </S.ActionButtonGroup>
                </S.ButtonSection>
                <S.ProcessSection>
                  <span className="process-label">{t('Approval_Detail_Content')}</span>
                  <StyledTextField
                    multiline
                    rows={4}
                    placeholder={t('Approval_Detail_Process_Placeholder')}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    fullWidth
                  />
                </S.ProcessSection>

                <S.ButtonSection2>
                  <S.ActionButtonGroup>
                    <S.ProcessButton
                      variant="contain"
                      size="large"
                      onClick={handleClickProcessBtn}
                      disabled={!selectedAction}
                    >
                      {t('Approval_Detail_Process_Submit')}
                    </S.ProcessButton>
                  </S.ActionButtonGroup>
                </S.ButtonSection2>
              </S.ProcessFrame>
            )}
          </>
        )}
      </S.Body>
    </S.Container>
  );
});

const ApprovalStepContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  background-color: ${({ theme: { colors } }) => colors['bg-white']};

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 5px 5px;
  }

  &:first-of-type {
    border-radius: 5px 5px 0 0;
  }
`;

const ApprovalStepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-light']};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.54;
  letter-spacing: 0.015em;
  flex-shrink: 0;
`;

const ApprovalStepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const ApproverInfo = styled.div`
  font-family: 'Pretendard';
  font-size: 14px;
  font-weight: 700;
  line-height: 1.43;
  letter-spacing: 0.011em;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ApprovalStepStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ApprovalStepUpdatedAt = styled.span`
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.54;
  letter-spacing: 0.012em;
  color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  width: 116px;
`;

const ApprovalStepComment = styled.div`
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 500;
  line-height: 1.54;
  letter-spacing: 0.012em;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  word-break: break-word;
`;

const StyledTextField = styled(TextField)`
  background: ${({ theme }) => theme.colors['bg-gray-lighter']};

  & .MuiInputBase-root textarea::placeholder {
    font-family: 'Pretendard';
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
`;
